import { PlusOutlined } from '@ant-design/icons';
import { CATEGORY_PREFIX, Product, ProductFamily, SortValues } from '@omniafishing/core';
import { Col, Pagination, Row, Tree } from 'antd';
import { DataNode } from 'antd/lib/tree';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListAttributionUuid } from '../../hooks/use_list_attribution_uuid';
import { useListViewed } from '../../hooks/use_list_viewed';
import { useQueryString } from '../../hooks/use_query_string';
import { RecommendationsFacets, RECOMMENDATIONS_PAGE_SIZE_DEFAULT } from '../../lib/api';
import { OmniaQueryParams } from '../../lib/query_string';
import { scrollToElementTop } from '../../lib/scroll';
import { WebAnalytics } from '../../lib/web_analytics';
import { getCategories, getSubcategories } from '../../redux/reference_data';
import { CollapsiblePanel } from '../collapsible_panel/collapsible_panel';
import { ContentWrapper } from '../content_wrapper/content_wrapper';
import { ProductsGroupCard } from '../products_group_card/products_group_card';
import { PRODUCTS_NAV_HEIGHT_APPROX } from '../products_nav/products_nav';
import { ShopNav } from '../shop_nav/shop_nav';
import { SortResults } from '../sort_results/sort_results';
import styles from './tag_products_grid.less';

interface TagProductsGridProps {
  defaultSortValue: SortValues;
  page: number;
  perPage: number;
  productGroups: { products: Product[]; product_family: ProductFamily }[];
  tag: string;
  facetSubcategories: Record<string, number>;
  totalProductsCount: number;
}

export const TagProductsGrid = (props: TagProductsGridProps) => {
  const { defaultSortValue, page, perPage, productGroups, facetSubcategories, totalProductsCount } =
    props;
  const divRef = useRef<HTMLDivElement>();
  const allSubcategories = useSelector(getSubcategories);
  const allCategories = useSelector(getCategories);
  const { getCurrentQuery, replaceQueryString, pushQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<{
    [OmniaQueryParams.subcategories]: string[];
    [RecommendationsFacets.sort]: string;
  }>();
  const { coupon, createListId } = useListViewed();

  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(
    currentQuery.subcategories || []
  );

  const allProductSubcategories = Object.entries(facetSubcategories).map((subcat) => {
    return {
      name: subcat[0],
      count: subcat[1],
      display_name: allSubcategories.find((sc) => sc.name === subcat[0]).display_name,
    };
  });

  const productSubcategoriesInCategories = () => {
    const subcatsInCategories = allCategories.map((category) => {
      const categorySubcategories = category.subcategories.map((subcat) => subcat.name);
      if (category) {
        return {
          name: category.name,
          display_name: category.display_name,
          subcategories: allProductSubcategories.filter((productSubcategory) =>
            categorySubcategories.includes(productSubcategory.name)
          ),
        };
      }
    });

    return subcatsInCategories.filter(Boolean).filter((cat) => cat.subcategories.length);
  };

  const onSubcategoryChange = (checkedKeys: string[]) => {
    const selectedSubcats = checkedKeys.filter((key) => !key.includes(CATEGORY_PREFIX));
    setSelectedSubcategories(selectedSubcats);
    replaceQueryString({
      [OmniaQueryParams.subcategories]: selectedSubcats,
      [OmniaQueryParams.pg]: 1,
    });
  };

  const list_uuid = useListAttributionUuid();

  const products = productGroups.map((pg) => pg.products).flat();

  useEffect(() => {
    WebAnalytics.productListViewed({
      products,
      coupon,
      list_id: createListId('other'),
      list_uuid,
    });
  }, [products.map((p) => p.sku)]);

  const treeData: DataNode[] = productSubcategoriesInCategories().map((category) => {
    return {
      title: category.display_name,
      key: `${CATEGORY_PREFIX}${category.name}`,
      selectable: false,
      active: true,
      children: category.subcategories.map((subcategory) => {
        return {
          title: subcategory.display_name,
          key: subcategory.name,
          selectable: false,
          active: true,
        };
      }),
    };
  });

  const scrollTop = () => {
    scrollToElementTop(divRef.current, PRODUCTS_NAV_HEIGHT_APPROX);
  };

  return (
    <ContentWrapper>
      <Row gutter={0}>
        <Col xs={24} sm={6} ref={divRef}>
          <CollapsiblePanel title="Categories" defaultOpenDesktop={true}>
            <Tree
              checkable
              onCheck={onSubcategoryChange}
              checkedKeys={selectedSubcategories}
              className={styles.tree}
              switcherIcon={<PlusOutlined />}
              treeData={treeData}
            />
          </CollapsiblePanel>
          <CollapsiblePanel title="Shop" defaultOpenDesktop={true}>
            <ShopNav />
          </CollapsiblePanel>
        </Col>
        <Col xs={24} sm={18}>
          <div style={{ paddingBottom: 12 }}>
            <SortResults
              defaultSortValue={defaultSortValue}
              setQueryString={false}
              onChange={(val) => {
                pushQueryString({
                  [OmniaQueryParams.pg]: 1,
                  [OmniaQueryParams.sort]: val,
                });
              }}
            />
          </div>
          <div className={styles.recommendations}>
            {productGroups.map((productGroup, i) => (
              <div
                className={styles.product}
                key={productGroup.products.map((p) => p.sku).join('-')}
              >
                <ProductsGroupCard
                  products={productGroup.products}
                  list_uuid={list_uuid}
                  position={i}
                  price_pro={productGroup.product_family.price_pro}
                />
              </div>
            ))}
          </div>
          <div className={styles.pagination}>
            <Pagination
              current={page}
              pageSize={perPage || RECOMMENDATIONS_PAGE_SIZE_DEFAULT}
              total={totalProductsCount}
              showSizeChanger={false}
              hideOnSinglePage
              onChange={(p) => {
                scrollTop();
                pushQueryString({
                  [OmniaQueryParams.pg]: p,
                });
              }}
              itemRender={(current, type, originalElement) => {
                if (type === 'prev') {
                  return <a rel="nofollow">Previous</a>;
                }
                if (type === 'next') {
                  return <a rel="nofollow">Next</a>;
                }
                return originalElement;
              }}
            />
          </div>
        </Col>
      </Row>
    </ContentWrapper>
  );
};
